/**
 * Zentrale OG-/Twitter-Bild-Map.
 *
 * Regeln:
 * - Key = Route-Pfad OHNE Locale-Prefix, immer mit führendem Slash
 *   ("/", "/about", "/blog/hubspot-starter").
 * - Alle Bild-URLs sind ABSOLUT auf die Produktions-Domain. LinkedIn, Slack und
 *   WhatsApp ignorieren relative og:image-URLs; auf Deploy-Previews wäre eine
 *   Preview-Host-URL für Scraper ohnehin wertlos.
 * - Jedes Motiv ist ein PNG im Format 1200 x 630 (1,91:1). Kein SVG, kein WebP —
 *   die rendert LinkedIn/Facebook/WhatsApp nicht.
 * - Routen ohne Eintrag fallen automatisch auf OG_DEFAULT zurück.
 *
 * Neue Route? -> Eintrag hier + 1200x630-PNG unter public/assets/og/.
 */

export const OG_SITE_ORIGIN = "https://prometheus.marketing";

/** Von Open Graph deklarierte Bildmaße. Gilt für jedes Motiv in dieser Map. */
export const OG_IMAGE_WIDTH = 1200;
export const OG_IMAGE_HEIGHT = 630;
export const OG_IMAGE_TYPE = "image/png";

export interface OgImage {
  /** Absolute URL des Motivs. */
  image: string;
  /** Alt-Text, wird als og:image:alt und twitter:image:alt gerendert. */
  alt: string;
}

/** Macht aus einem root-relativen Asset-Pfad eine absolute Produktions-URL. */
export function toAbsoluteOgUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) {
    return pathOrUrl;
  }

  return `${OG_SITE_ORIGIN}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
}

export const OG_DEFAULT: OgImage = {
  image: "https://prometheus.marketing/assets/og/og-default.png",
  alt: "Prometheus Marketing: ABM & B2B-Marketing für DACH",
};

/**
 * Route -> eigenes Motiv. Nur Routen, die ein eigenes Bild HABEN, stehen hier.
 * Alles andere bekommt OG_DEFAULT über resolveOgImage().
 */
export const OG_IMAGES: Record<string, OgImage> = {
  // --- Blogartikel mit eigenem Motiv (Motiv 1, 1200x630) ---------------------
  "/blog/b2b-leadgenerierung-linkedin": {
    image: toAbsoluteOgUrl("/assets/blog/b2b-leadgenerierung-linkedin/motiv-1-og.png"),
    alt: "Reichweite ist nicht gleich Pipeline",
  },
  "/blog/hubspot-starter": {
    image: toAbsoluteOgUrl("/assets/blog/hubspot-starter/motiv-1-og.png"),
    alt: "HubSpot Starter richtig aufsetzen",
  },
  "/blog/icp-erstellen-abm-zielliste": {
    image: toAbsoluteOgUrl("/assets/blog/icp-erstellen-abm-zielliste/motiv-1-og.png"),
    alt: "Ohne Zielliste ist ABM nur teures Streuen",
  },
  "/blog/linkedin-ads-agentur-b2b": {
    // TODO asset: Datei liegt als 2400x1260-Master vor (identisches 1,91:1-Ratio).
    // Für saubere Übereinstimmung mit og:image:width/height auf 1200x630
    // herunterrechnen — Motiv selbst bleibt unverändert.
    image: toAbsoluteOgUrl("/assets/blog/linkedin-ads-agentur-b2b/motiv-1-og.png"),
    alt: "LinkedIn Ads für B2B: Budget, Zielgruppe, Pipeline",
  },
  "/blog/linkedin-termine-generieren-telemarketing": {
    image: toAbsoluteOgUrl(
      "/assets/blog/linkedin-termine-generieren-telemarketing/motiv-1-og.png",
    ),
    alt: "Von LinkedIn-Kontakt zum Termin, ohne Kaltakquise-Reflex",
  },
  "/blog/thought-leadership-linkedin": {
    image: toAbsoluteOgUrl("/assets/blog/thought-leadership-linkedin/motiv-1-og.png"),
    alt: "Die beste Fachstimme sitzt selten im Chefbüro. Organigramm, in dem eine Fachstimme aus dem Team hervorgehoben ist",
  },
  "/blog/was-ist-revops-revenue-operations": {
    image: toAbsoluteOgUrl("/assets/blog/was-ist-revops-revenue-operations/motiv-1-og.png"),
    alt: "Drei Silos, ein Umsatz: Revenue Operations",
  },
  "/blog/webinar-leadgenerierung-b2b": {
    image: toAbsoluteOgUrl("/assets/blog/webinar-leadgenerierung-b2b/motiv-1-og.png"),
    alt: "Webinar-Funnel im B2B: 40 Prozent Showrate und 10 Prozent Termine aus Live-Teilnehmern",
  },
  "/blog/website-besucher-identifizieren-ip-tracking": {
    // Bewusst das vorhandene PNG-Motiv, NICHT das Inline-SVG
    // (/assets/blog/ip-tracking/thumbnail-revops.svg) — SVG wird von LinkedIn,
    // Facebook und WhatsApp nicht gerendert.
    image: toAbsoluteOgUrl("/assets/blog/ip-tracking/motiv-1-og.png"),
    alt: "98 Prozent der Website-Besucher bleiben anonym",
  },
};

/** Normalisiert einen Pfad auf das Key-Format der Map (ohne Locale-Prefix). */
export function normalizeOgKey(pathWithoutLocale: string): string {
  const trimmed = pathWithoutLocale.replace(/^\/+/, "").replace(/\/+$/, "");

  return trimmed ? `/${trimmed}` : "/";
}

/**
 * Motiv für eine Route. `pathWithoutLocale` ist der Pfad OHNE /de|/at|/ch|/en.
 * Unbekannte Routen bekommen OG_DEFAULT.
 */
export function resolveOgImage(pathWithoutLocale: string): OgImage {
  return OG_IMAGES[normalizeOgKey(pathWithoutLocale)] ?? OG_DEFAULT;
}
